import { extractJson } from '../utils/jsonParser.js';

const VALID_CONFIDENCES = ['certain', 'likely', 'possible', 'wrong'];
const VALID_ANSWERS = ['yes', 'no', 'unknown', 'irrelevant'];

const QUESTION_SYSTEM_PROMPT = `You are the host of a "20 questions" style celebrity guessing game.
You have secretly chosen a target figure. The target may be a real famous person OR a fictional character (from novels, movies, TV series, video games, animation, anime/manga, fairy tales, myths, legends, etc.). The player will ask you yes/no questions about this figure.

Your task:
1. Answer the player's question truthfully based on your knowledge of the target figure.
2. For fictional characters, answer based on the character's canonical setting and storyline in their original work (canon), treating the work's world as the frame of reference.
3. If the player asks whether the target is a real person / is fictional (e.g. "是真实存在的人吗？", "是虚构角色吗？"), answer truthfully — a fictional character is NOT a real person. Never disguise this.
4. Return ONLY a JSON object with the following schema:
{
  "answer": "yes|no|unknown|irrelevant",
  "feedback": "string (1 sentence, in zh-CN, addressing the player directly)",
  "reason": "string (brief internal reason, in zh-CN)"
}

Answer rules:
- "yes": the fact is true for the target.
- "no": the fact is false for the target.
- "unknown": you are genuinely unsure or the fact is ambiguous.
- "irrelevant": the question is not about the figure's attributes (e.g. about the player's own knowledge, or about things unrelated to the figure).

Other rules:
- Keep feedback concise, friendly, and puzzle-master style.
- NEVER mention the target's name, aliases, era, source work title, or any identifying detail in feedback.
- If the question is about gender/nationality/time period/field, answer in general terms only, e.g. "是的，这位人物是男性。" Do NOT say "是的，苏轼是男性。"
- Bad example (NEVER do this): "是的，苏轼是男性。"
- Good example: "是的，这位人物是男性。"
- Use zh-CN for all text.
- Output ONLY the JSON object, no markdown, no code fences.`;

const GUESS_SYSTEM_PROMPT = `You are the judge of a "20 questions" style celebrity guessing game.

You are given:
- TARGET_CELEBRITY: the canonical name, aliases, and a brief summary of the target. The target may be a real famous person OR a fictional character (from novels, movies, games, anime, fairy tales, myths, etc.).
- HISTORY: all previous questions and answers, plus any previous guesses.
- CURRENT_GUESS: the player's latest name guess.

Your task:
1. Determine if CURRENT_GUESS matches TARGET_CELEBRITY.
2. Consider aliases, different Chinese translations, common nicknames, titles/epithets, and partial names that uniquely identify the same figure (e.g. "齐天大圣" matches 孙悟空; "哈利" may match 哈利·波特 if unambiguous).
3. Return ONLY a JSON object with the following schema:
{
  "correct": boolean,
  "matchConfidence": "certain|likely|possible|wrong",
  "feedback": "string (1-2 sentences, in zh-CN, addressing the player directly)",
  "reason": "string (brief internal reason for your judgment, in zh-CN)"
}

Rules for feedback:
- If correct: congratulate the player, do NOT continue describing the person.
- If wrong: say it is wrong, never reveal the target name.
- Always keep the same tone: friendly, concise, puzzle-master style.
- Do not include the answer in feedback.
- Use zh-CN for all text.
- Output ONLY the JSON object, no markdown, no code fences.`;

function buildTargetBlock(celebrity) {
  return [
    'TARGET_CELEBRITY:',
    `name: ${celebrity.name}`,
    `zhName: ${celebrity.zhName}`,
    `enName: ${celebrity.enName}`,
    `aliases: ${celebrity.aliases.join(', ')}`,
    `era: ${celebrity.era}`,
    `field: ${celebrity.field}`,
    `brief: ${celebrity.brief}`,
  ].join('\n');
}

function buildHistoryBlock(history) {
  if (!history || history.length === 0) {
    return 'HISTORY: (none)';
  }
  const lines = history.map((h, i) => {
    if (h.type === 'question') {
      return `${i + 1}. Player asked: "${h.input}" -> Answer: ${h.answer}`;
    }
    return `${i + 1}. Player guessed: "${h.input}" -> Correct: ${h.correct ? 'yes' : 'no'}`;
  });
  return `HISTORY:\n${lines.join('\n')}`;
}

function buildQuestionUserPrompt(celebrity, history, question) {
  return [
    buildTargetBlock(celebrity),
    '',
    buildHistoryBlock(history),
    '',
    `CURRENT_QUESTION: "${question}"`,
    '',
    'Answer this question and return JSON.',
  ].join('\n');
}

function buildGuessUserPrompt(celebrity, history, guess) {
  return [
    buildTargetBlock(celebrity),
    '',
    buildHistoryBlock(history),
    '',
    `CURRENT_GUESS: "${guess}"`,
    '',
    'Judge this guess and return JSON.',
  ].join('\n');
}

function sanitizeFeedback(feedback, celebrity) {
  if (!feedback || !celebrity) return feedback;
  const names = [
    celebrity.name,
    celebrity.zhName,
    celebrity.enName,
    ...(celebrity.aliases || []),
  ].filter(Boolean).sort((a, b) => b.length - a.length);

  let sanitized = feedback;
  for (const name of names) {
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escaped, 'gi');
    sanitized = sanitized.replace(regex, '这位人物');
  }
  return sanitized;
}

function validateQuestionResult(obj) {
  if (!obj || typeof obj !== 'object') {
    throw new Error('Invalid question response');
  }
  if (!VALID_ANSWERS.includes(obj.answer)) {
    throw new Error(`Invalid answer: ${obj.answer}`);
  }
  if (!obj.feedback || typeof obj.feedback !== 'string') {
    throw new Error('Missing or invalid "feedback" field');
  }
  return {
    answer: obj.answer,
    feedback: obj.feedback,
    reason: obj.reason || '',
  };
}

function validateGuessResult(obj) {
  if (!obj || typeof obj !== 'object') {
    throw new Error('Invalid guess response');
  }
  if (typeof obj.correct !== 'boolean') {
    throw new Error('Missing or invalid "correct" field');
  }
  if (!VALID_CONFIDENCES.includes(obj.matchConfidence)) {
    throw new Error(`Invalid matchConfidence: ${obj.matchConfidence}`);
  }
  if (!obj.feedback || typeof obj.feedback !== 'string') {
    throw new Error('Missing or invalid "feedback" field');
  }
  return {
    correct: obj.correct,
    matchConfidence: obj.matchConfidence,
    feedback: obj.feedback,
    reason: obj.reason || '',
  };
}

export async function answerQuestion(client, { celebrity, history, question }) {
  const messages = [
    { role: 'system', content: QUESTION_SYSTEM_PROMPT },
    { role: 'user', content: buildQuestionUserPrompt(celebrity, history, question) },
  ];

  const response = await client.send(messages, {
    responseFormat: { type: 'json_object' },
  });

  const parsed = extractJson(response);
  const result = validateQuestionResult(parsed);
  return {
    type: 'question',
    input: question,
    answer: result.answer,
    feedback: sanitizeFeedback(result.feedback, celebrity),
    reason: result.reason,
  };
}

export async function verifyGuess(client, { celebrity, history, guess }) {
  const messages = [
    { role: 'system', content: GUESS_SYSTEM_PROMPT },
    { role: 'user', content: buildGuessUserPrompt(celebrity, history, guess) },
  ];

  const response = await client.send(messages, {
    responseFormat: { type: 'json_object' },
  });

  const parsed = extractJson(response);
  const result = validateGuessResult(parsed);
  const feedback = result.correct
    ? result.feedback
    : sanitizeFeedback(result.feedback, celebrity);
  return {
    type: 'guess',
    input: guess,
    correct: result.correct,
    matchConfidence: result.matchConfidence,
    feedback,
    reason: result.reason,
  };
}
